import React, { useState, useRef, useEffect } from "react";
import { Zap, Settings } from "lucide-react";
import "./_actions-button.scss";

const ActionsButton = ({
  sections = [],
  className = "",
  size = "small",
  variant = "outlined",
  disabled = false,
  showManageBundles = false,
  onManageBundles,
  canManageBundle = false,
  label,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleActionClick = (action) => {
    if (action.onClick) {
      action.onClick();
    }
    setIsOpen(false);
  };

  const handleManageBundles = () => {
    if (onManageBundles) {
      onManageBundles();
    }
    setIsOpen(false);
  };

  const getButtonClass = () => {
    const baseClass = "actions-button";
    const sizeClass = `actions-button--${size}`;
    const variantClass = `actions-button--${variant}`;
    const stateClass = isOpen ? "actions-button--open" : "";
    const disabledClass = disabled ? "actions-button--disabled" : "";

    return `${baseClass} ${sizeClass} ${variantClass} ${stateClass} ${disabledClass} ${className}`.trim();
  };

  // Create all sections including manage bundles if needed
  const allSections = [...sections];

  if (showManageBundles) {
    allSections.push({
      title: "Bundle Management",
      items: [
        {
          label: "Manage Bundles",
          icon: <Settings size={16} />,
          onClick: handleManageBundles,
          disabled: !canManageBundle,
          title: canManageBundle
            ? ""
            : "You don't have permission to manage bundles. Please contact your admin for access.",
        },
      ],
    });
  }

  return (
    <div className="actions-button-wrapper" ref={dropdownRef}>
      <button
        className={`${getButtonClass()} ${label ? "actions-button--labeled" : ""}`.trim()}
        onClick={handleToggle}
        disabled={disabled}
        type="button"
        {...props}
      >
        {label ? label : <Zap size={16} />}
      </button>

      {isOpen && (
        <div className="actions-header-dropdown">
          {allSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="dropdown-section">
              {section.title && (
                <div className="actions-section-header">
                  <span className="actions-section-title">{section.title}</span>
                </div>
              )}
              <div className="actions-section-items">
                {section.items.map((item, itemIndex) => (
                  <button
                    key={itemIndex}
                    className="actions-dropdown-item"
                    onClick={() => handleActionClick(item)}
                    disabled={item.disabled}
                    title={item.title}
                  >
                    {item.icon && (
                      <span className="item-icon">{item.icon}</span>
                    )}
                    <span className="item-text">{item.label}</span>
                    {item.rightIcon && (
                      <span className="item-right-icon">{item.rightIcon}</span>
                    )}
                  </button>
                ))}
              </div>
              {sectionIndex < allSections.length - 1 && (
                <div className="actions-section-divider" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActionsButton;
